import { useQuery } from '@tanstack/react-query'
import { Search } from 'lucide-react'
import { Dialog as RadixDialog } from 'radix-ui'
import { useEffect, useId, useMemo, useState } from 'react'
import { useNavigate } from 'react-router'

import { endpoints } from '@/api'
import { keys } from '@/api/keys'
import { useCan, useCapabilities } from '@/auth'
import { groupedNavItems } from '@/app/route-registry'
import { SETTINGS_CARDS } from '@/features/settings/settings-hub'
import { hasAnyPermission } from '@/auth/capabilities'

interface Command {
  readonly id: string
  readonly label: string
  readonly hint: string
  readonly to: string
}

/** Cmd/Ctrl-K on every platform. `metaKey` on macOS, `ctrlKey` elsewhere — both accepted. */
export function isPaletteShortcut(event: KeyboardEvent): boolean {
  return (event.metaKey || event.ctrlKey) && !event.altKey && event.key.toLowerCase() === 'k'
}

/**
 * The command palette (FRONTEND_PLAN F18): every route the caller may open, plus customer search
 * when they hold `customers:read`. Routes come from the same registries the sidebar and the
 * settings hub use, so it can never offer a page the capabilities would refuse.
 */
export function CommandPalette({
  open,
  onOpenChange,
}: {
  readonly open: boolean
  readonly onOpenChange: (open: boolean) => void
}) {
  const navigate = useNavigate()
  const capabilities = useCapabilities()
  const canCustomers = useCan('customers:read')
  const [term, setTerm] = useState('')
  const [active, setActive] = useState(0)
  const listId = useId()

  const routes = useMemo<Command[]>(() => {
    const nav = groupedNavItems(capabilities).flatMap((group) =>
      group.items.map((item) => ({
        id: item.path,
        label: item.label,
        hint: group.group,
        to: item.path,
      })),
    )
    const settings = SETTINGS_CARDS.filter((card) =>
      hasAnyPermission(capabilities, card.anyOf),
    ).map((card) => ({ id: card.to, label: card.title, hint: 'Settings', to: card.to }))
    return [...nav, ...settings.filter((s) => !nav.some((n) => n.to === s.to))]
  }, [capabilities])

  const query = term.trim()
  // Debounced like the customer picker: one request per pause, not per keystroke.
  const [debounced, setDebounced] = useState('')
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebounced(query)
    }, 200)
    return () => {
      clearTimeout(timer)
    }
  }, [query])
  const customers = useQuery({
    queryKey: keys.customers.list({ q: debounced, limit: 8, palette: true }),
    queryFn: () => endpoints.customers.list({ q: debounced, limit: 8 }),
    enabled: open && canCustomers && debounced.length >= 2,
  })

  const matches = useMemo<Command[]>(() => {
    const needle = query.toLowerCase()
    const routeHits = routes.filter(
      (r) =>
        needle === '' ||
        r.label.toLowerCase().includes(needle) ||
        r.hint.toLowerCase().includes(needle),
    )
    const customerHits = (customers.data ?? [])
      .filter((c) => !c.anonymised)
      .map((c) => ({
        id: `customer-${c.id}`,
        label: c.name,
        hint: `Customer · ${c.phone ?? c.email ?? ''}`,
        to: `/customers/${c.id}`,
      }))
    return [...routeHits, ...customerHits]
  }, [routes, customers.data, query])

  useEffect(() => {
    setActive(0)
  }, [query, open])
  useEffect(() => {
    if (!open) setTerm('')
  }, [open])

  const go = (command: Command | undefined): void => {
    if (command === undefined) return
    onOpenChange(false)
    void navigate(command.to)
  }

  return (
    <RadixDialog.Root open={open} onOpenChange={onOpenChange}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="fixed inset-0 z-50 bg-ink/40" />
        <RadixDialog.Content
          className="fixed top-[12vh] left-1/2 z-50 w-[min(640px,94vw)] -translate-x-1/2 overflow-hidden rounded-md border border-line bg-panel shadow-lg outline-none"
          aria-describedby={undefined}
        >
          <RadixDialog.Title className="sr-only">Go to</RadixDialog.Title>
          <div className="flex items-center gap-2 border-b border-line px-3">
            <Search aria-hidden className="size-4 text-muted-2" />
            <input
              role="combobox"
              aria-label="Go to"
              aria-expanded
              aria-controls={listId}
              aria-activedescendant={
                matches[active] === undefined ? undefined : `${listId}-${matches[active].id}`
              }
              aria-autocomplete="list"
              className="h-12 w-full bg-transparent text-[14px] outline-none placeholder:text-muted-2"
              placeholder={canCustomers ? 'Pages, or a customer name…' : 'Pages…'}
              value={term}
              onChange={(e) => {
                setTerm(e.target.value)
              }}
              onKeyDown={(e) => {
                if (e.key === 'ArrowDown') {
                  e.preventDefault()
                  setActive((i) => Math.min(i + 1, Math.max(matches.length - 1, 0)))
                } else if (e.key === 'ArrowUp') {
                  e.preventDefault()
                  setActive((i) => Math.max(i - 1, 0))
                } else if (e.key === 'Enter') {
                  e.preventDefault()
                  go(matches[active])
                }
              }}
            />
            <kbd className="hidden rounded-sm border border-line px-1.5 py-0.5 font-mono text-[10.5px] text-muted-2 sm:inline">
              Esc
            </kbd>
          </div>
          <ul
            id={listId}
            role="listbox"
            aria-label="Results"
            className="max-h-[50vh] overflow-y-auto py-1"
          >
            {matches.length === 0 && (
              <li className="px-3 py-3 text-[13px] text-muted">
                {customers.isFetching ? 'Searching…' : 'Nothing matches.'}
              </li>
            )}
            {matches.map((command, index) => (
              <li
                key={command.id}
                id={`${listId}-${command.id}`}
                role="option"
                aria-selected={index === active}
                className={`flex cursor-pointer items-center justify-between gap-3 px-3 py-2 text-[13px] ${index === active ? 'bg-paper text-ink' : 'text-ink-2'}`}
                onMouseEnter={() => {
                  setActive(index)
                }}
                onMouseDown={(e) => {
                  e.preventDefault()
                  go(command)
                }}
              >
                <span className="font-medium">{command.label}</span>
                <span className="text-[11.5px] text-muted-2">{command.hint}</span>
              </li>
            ))}
          </ul>
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  )
}
